import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Eye } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import VoucherStatusChip from "../../components/VoucherStatusChip";
import { PETTY_CASH_RECEIPT_FILTERS } from "../../pettyCashStatusMaps";
import { useListPettyCashVouchersQuery } from "../../pettyCashApi";
import { useCanCreateVoucher } from "../../usePettyCashPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "APPROVED", label: "Approved" },
  { value: "PAID", label: "Paid" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

const money = (n) => Number(n || 0).toFixed(2);

const VoucherCompactList = () => {
  const canCreate = useCanCreateVoucher();
  const navigate = useNavigate();
  const { recordGuid: selectedRecordGuid } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [receiptStatus, setReceiptStatus] = useState("");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading } = useListPettyCashVouchersQuery({
    branchId: selectedBranchId ?? undefined,
    status: status || undefined,
    receiptStatus: receiptStatus || undefined,
    pageSize: 200,
  });

  const items = (data?.items || []).filter(
    (v) =>
      !search ||
      v.voucherNumber.toLowerCase().includes(search.toLowerCase()) ||
      v.description?.toLowerCase().includes(search.toLowerCase()) ||
      v.requestedByName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MasterDetailListShell
      title="Petty Cash Vouchers"
      addButton={
        canCreate && (
          <button type="button" className="btn btn-added" onClick={() => navigate(paths.pettyCashVoucherNew)}>
            <PlusCircle className="me-2" size={16} />
            New Voucher
          </button>
        )
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search voucher, description or requester"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="d-flex gap-2 mt-2">
            <select className="form-control form-control-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {/* Separate from the status filter on purpose - "paid" and "receipt pending"
                are two different things about the same voucher. */}
            <select
              className="form-control form-control-sm"
              value={receiptStatus}
              onChange={(e) => setReceiptStatus(e.target.value)}
            >
              {PETTY_CASH_RECEIPT_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      }
      isLoading={isLoading}
      emptyLabel="No vouchers match these filters."
      items={items}
      getKey={(v) => v.recordGuid}
      resetKey={`${search}|${status}|${receiptStatus}|${selectedBranchId}`}
      renderItem={(v) => {
        const isSelected = String(v.recordGuid) === String(selectedRecordGuid);
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.pettyCashVoucherDetail(v.recordGuid))}
            avatarLabel={(v.requestedByName || "?").trim().slice(0, 2).toUpperCase()}
            // What the money was for leads, with the amount beside it; category,
            // voucher number and branch are the detail line (2026-09-12). The
            // amount is the furthest the voucher has got - paid, else approved,
            // else requested.
            title={
              <>
                {v.description || v.voucherNumber}{" "}
                <span className="tx-money-out ms-1">{money(v.paidAmount ?? v.approvedAmount ?? v.requestedAmount)}</span>
              </>
            }
            subtitle={
              <>
                {v.categoryName ? <>{v.categoryName} &middot; </> : null}
                {v.voucherNumber} &middot; {v.branchCode}
                {Number(v.returnedAmount || 0) > 0 && (
                  <span className="text-success"> &middot; {money(v.returnedAmount)} returned</span>
                )}
              </>
            }
            badge={
              // One pill, never two - two badges wrapped onto a second line on narrower
              // rows and left the list with uneven row heights.
              <span className="me-2">
                <VoucherStatusChip status={v.status} receiptStatus={v.receiptStatus} />
              </span>
            }
            actions={
              <button
                type="button"
                className="btn btn-link p-1"
                title="View"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(paths.pettyCashVoucherDetail(v.recordGuid));
                }}
              >
                <Eye size={15} />
              </button>
            }
          />
        );
      }}
    />
  );
};

export default VoucherCompactList;
